import 'reflect-metadata';
import BeforeInitBehaviors from '../../behaviors/beforeInitBehaviors';
import ClientsInitializer from './clients/ClientsInitializer';
import { ClientsType } from './clients/ClientsType';
import ManifestToCommonsAdapter from './ManifestToCommonsAdapter';
import RepositoryInitializer from './repositories/RepositoriesInitializer';
import { RepositoriesType } from './repositories/RepositoriesType';
import ServicesInitializer from './services/ServicesInitializer';
import { ServicesType } from './services/ServicesType';
import { ManifestType } from '@clientos/types';
import ContainersInitializer, {
  ContainersType
} from './containers/ContainersInitializer';

let services: ServicesType;
let clients: ClientsType;
let repositories: RepositoriesType;
let containers: ContainersType;

/**
 *  * This function is responsible for creating and binding the services, clients, and repositories.
 * It uses the provided manifest to initialize the services and bind them to the clients and repositories.
 * It also registers the services as overridable singletons.
 * @param manifest:ManifestType
 */
export async function _createCommonsModules(
  manifest: ManifestType
): Promise<void> {
  // Currently it's using the manifest here, but it's already using an adapter
  const commonsInitializeProps = await ManifestToCommonsAdapter.convert(
    manifest
  );

  // Instantiation Phase
  repositories = await RepositoryInitializer.instantiate();

  ServicesInitializer.registerOverridableSingletons();
  services = await ServicesInitializer.instantiate(commonsInitializeProps);

  const { applicationService, authProviderService } = services;

  clients = ClientsInitializer.instantiate({
    applicationService,
    authProviderService
  });

  containers = ContainersInitializer.instantiate(services);

  // Configuration Phase:
  // Resolve/set dependencies
  await ServicesInitializer.resolveDependencies({
    services,
    clients,
    repositories
  });

  await ContainersInitializer.resolveDependencies({
    services,
    clients,
    repositories,
    containers
  });
}

export async function initializeCommons(manifest: ManifestType): Promise<void> {
  await _createCommonsModules(manifest);

  const {
    graphQLService,
    sessionService,
    webServiceRouting,
    navigationService,
    localizationService,
    loggerService,
    routesService,
    serviceWorkerService,
    tenantHandlerService,
    featureFlagService
  } = services;

  // NOW, we Start the initialization of the services
  // Initialize priority services
  // >> keep this at first
  await localizationService.init();
  navigationService.init();
  await graphQLService.init();

  await sessionService.init();

  // Run Before initialization behaviors
  await BeforeInitBehaviors.init();

  // Initialize other services
  webServiceRouting.initialize();
  await tenantHandlerService.init(sessionService);
  serviceWorkerService.init();
  featureFlagService.init();
  await loggerService.init();

  // is this a middle-init behavior?
  const navigation = navigationService;
  const currentPath = navigation.location.pathname;
  const { tenantHandlerOverride } =
    (await routesService?.findRouteWithPriority?.(currentPath, false)) || {};
  await tenantHandlerService?.setTenantHandlerKey(tenantHandlerOverride);
}

export function getServices(): ServicesType {
  if (!services) throw new Error('Initialize the services first.');
  return services;
}

export function getClients(): ClientsType {
  if (!clients) throw new Error('Initialize the services first.');
  return clients;
}

export function getRepositories(): RepositoriesType {
  if (!repositories) throw new Error('Initialize the services first.');
  return repositories;
}

export function getContainers(): ContainersType {
  if (!containers) throw new Error('Initialize the services first.');
  return containers;
}

export function _getCommonsModules(): {
  services: ServicesType;
  clients: ClientsType;
  repositories: RepositoriesType;
} {
  if (!services || !clients || !repositories)
    throw new Error('Initialize the services first.');
  return { services, clients, repositories };
}
